import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import { Box, ButtonBase, Chip, Collapse, Stack, Typography } from '@mui/material';
import React from 'react';
import { useHistory } from 'react-router-dom';
import AudioPlayButton from '../components/AudioPlayButton';
import PronunciationDetail from '../components/exercises/PronunciationDetail';
import PhoneFrame from '../components/PhoneFrame';
import ProgressRing from '../components/ProgressRing';
import { mockFeedbackAttempts, SpeakingAttemptFeedback } from '../mockFeedback';
import { BLUE, cardSx, GREEN, INTER, NAVY, POPPINS, SLATE, SUCCESS_TEXT, SURFACE_SOFT, YELLOW } from '../studentStyles';

// One submitted speaking recording: AI feedback right away, teacher review when
// it lands — the student-side flip of the teacher mockup's Speaking review page.
function AttemptCard({ attempt }: { attempt: SpeakingAttemptFeedback }) {
    const [detailOpen, setDetailOpen] = React.useState(false);
    const reviewed = attempt.review != null;

    return (
        <Box sx={{ ...cardSx, p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 11, letterSpacing: '0.08em', color: SLATE, flex: 1 }}>
                    {attempt.exerciseLabel.toUpperCase()}
                </Typography>
                <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE }}>
                    {attempt.submitted}
                </Typography>
            </Box>

            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 20, color: NAVY, mt: 1.5, lineHeight: 1.4 }}>
                {attempt.sentenceHanzi}
            </Typography>
            <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.25 }}>
                {attempt.sentencePinyin}
            </Typography>
            <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 1 }}>
                Task: {attempt.promptEnglish}
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mt: 2 }}>
                <ProgressRing percent={attempt.review?.teacherScore ?? attempt.aiScore} size={56} thickness={4} showLabel />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Chip
                        icon={reviewed
                            ? <CheckCircleRoundedIcon sx={{ fontSize: 16 }} />
                            : <HourglassTopRoundedIcon sx={{ fontSize: 16 }} />}
                        label={reviewed ? 'Reviewed by your teacher' : 'Awaiting teacher review'}
                        size="small"
                        sx={{
                            maxWidth: '100%', height: 'auto', minHeight: 24, '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 },
                            fontFamily: INTER, fontWeight: 600, fontSize: 12,
                            color: reviewed ? SUCCESS_TEXT : NAVY,
                            backgroundColor: reviewed ? `${GREEN}14` : `${YELLOW}3D`,
                            '& .MuiChip-icon': { color: reviewed ? GREEN : NAVY },
                        }}
                    />
                    <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, mt: 0.5 }}>
                        {attempt.review ? `Reviewed ${attempt.review.reviewedOn}` : 'AI score for now'}
                    </Typography>
                </Box>
                <AudioPlayButton ariaLabel="Replay your recording" size={40} />
            </Box>

            <Box sx={{ mt: 2, p: 2, borderRadius: '16px', backgroundColor: SURFACE_SOFT }}>
                <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 11, letterSpacing: '0.08em', color: SLATE }}>
                    AI FEEDBACK
                </Typography>
                <Typography sx={{ fontFamily: INTER, fontSize: 14, color: NAVY, mt: 0.75, lineHeight: 1.5 }}>
                    {attempt.aiFeedback}
                </Typography>
            </Box>

            {attempt.review && (
                <Box sx={{ mt: 1.5, p: 2, borderRadius: '16px', backgroundColor: `${GREEN}14` }}>
                    <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 11, letterSpacing: '0.08em', color: SUCCESS_TEXT }}>
                        FROM YOUR TEACHER
                    </Typography>
                    <Typography sx={{ fontFamily: INTER, fontSize: 14, color: NAVY, mt: 0.75, lineHeight: 1.5 }}>
                        {attempt.review.comment}
                    </Typography>
                </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                <ButtonBase
                    onClick={() => setDetailOpen(prev => !prev)}
                    aria-expanded={detailOpen}
                    sx={{ minHeight: 44, borderRadius: '999px', px: 1.5, py: 0.5, fontFamily: INTER, fontWeight: 700, fontSize: 13, color: BLUE, gap: 0.5 }}
                >
                    Pronunciation details
                    <ExpandMoreRoundedIcon sx={{ fontSize: 20, transform: detailOpen ? 'rotate(180deg)' : 'none', transition: 'transform 200ms ease' }} />
                </ButtonBase>
            </Box>
            <Collapse in={detailOpen}>
                <Box sx={{ mt: 1.5 }}>
                    <PronunciationDetail grades={attempt.grades} />
                </Box>
            </Collapse>
        </Box>
    );
}

export default function StudentFeedback() {
    const history = useHistory();

    return (
        <PhoneFrame title="Teacher feedback" onBack={() => history.push('/mockup/v2/student')}>
            <Stack spacing={2}>
                {mockFeedbackAttempts.length === 0 ? (
                    <Box sx={{ ...cardSx, p: 3, textAlign: 'center' }}>
                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY }}>
                            No recordings yet
                        </Typography>
                        <Typography sx={{ fontFamily: INTER, fontSize: 14, color: SLATE, mt: 0.5 }}>
                            Finish a speaking exercise and check back.
                        </Typography>
                    </Box>
                ) : (
                    mockFeedbackAttempts.map(attempt => <AttemptCard key={attempt.id} attempt={attempt} />)
                )}
            </Stack>
        </PhoneFrame>
    );
}
